var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
class ThemeEvents {
}
/** @static @constant {string} Event triggered when a variant is selected */
__publicField(ThemeEvents, "variantSelected", "variant:selected");
/** @static @constant {string} Event triggered when a variant is changed */
__publicField(ThemeEvents, "variantUpdate", "variant:update");
/** @static @constant {string} Event triggered when the cart items or quantities are updated */
__publicField(ThemeEvents, "cartUpdate", "cart:update");
/** @static @constant {string} Event triggered when a cart update fails */
__publicField(ThemeEvents, "cartError", "cart:error");
/** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
__publicField(ThemeEvents, "mediaStartedPlaying", "media:started-playing");
// Event triggered when quantity-selector value is changed
__publicField(ThemeEvents, "quantitySelectorUpdate", "quantity-selector:update");
/** @static @constant {string} Event triggered when a predictive search is expanded */
__publicField(ThemeEvents, "megaMenuHover", "megaMenu:hover");
/** @static @constant {string} Event triggered when a zoom dialog media is selected */
__publicField(ThemeEvents, "zoomMediaSelected", "zoom-media:selected");
/** @static @constant {string} Event triggered when a discount is applied */
__publicField(ThemeEvents, "discountUpdate", "discount:update");
/** @static @constant {string} Event triggered when changing collection filters */
__publicField(ThemeEvents, "FilterUpdate", "filter:update");
class VariantSelectedEvent extends Event {
  /**
   * Creates a new VariantSelectedEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The option value id
   */
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true });
    this.detail = {
      resource
    };
  }
}
class VariantUpdateEvent extends Event {
  /**
   * Creates a new VariantUpdateEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The id of the variant
   * @param {boolean} resource.available - Whether the variant is available
   * @param {boolean} resource.inventory_management - Whether the variant has inventory management
   * @param {string} [resource.sku] - The SKU of the variant
   * @param {Object} [resource.featured_media] - The featured media of the variant
   * @param {string} [resource.featured_media.id] - The id of the featured media
   * @param {Object} [resource.featured_media.preview_image] - The preview image of the featured media
   * @param {string} [resource.featured_media.preview_image.src] - The src URL of the preview image
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} data - Additional event data
   * @param {Document} data.html - The new document fragment for the variant
   * @param {string} data.productId - The product ID of the updated variant, used to ensure the correct product form is updated
   * @param {Object} [data.newProduct] - If a new product was loaded as part of the variant update (combined listing)
   * @param {string} data.newProduct.id - The id of the new product
   * @param {string} data.newProduct.url - The url of the new product
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId,
      data: {
        html: data.html,
        productId: data.productId,
        newProduct: data.newProduct
      }
    };
  }
}
const _CartAddEvent = class _CartAddEvent extends Event {
  /**
   * Creates a new CartAddEvent
   * @param {Object} [resource] - The new cart object
   * @param {string} [sourceId] - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was added
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(_CartAddEvent.eventName, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data
      }
    };
  }
};
__publicField(_CartAddEvent, "eventName", ThemeEvents.cartUpdate);
let CartAddEvent = _CartAddEvent;
class CartUpdateEvent extends Event {
  /**
   * Creates a new CartUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was updated
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data
      }
    };
  }
}
class CartErrorEvent extends Event {
  /**
   * Creates a new CartErrorEvent
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {string} message - A message from the server response
   * @param {Object} description - Description from the server response
   * @param {Object} errors - Errors from the server response
   */
  constructor(sourceId, message, description, errors) {
    super(ThemeEvents.cartError, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        message,
        errors,
        description
      }
    };
  }
}
class QuantitySelectorUpdateEvent extends Event {
  /**
   * Creates a new QuantitySelectorUpdateEvent
   * @param {number} quantity - Quantity value
   * @param {number} [cartLine] - The id of the updated cart line
   */
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
    this.detail = {
      quantity,
      cartLine
    };
  }
}
class DiscountUpdateEvent extends Event {
  /**
   * Creates a new DiscountUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   */
  constructor(resource, sourceId) {
    super(ThemeEvents.discountUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId
    };
  }
}
class MediaStartedPlayingEvent extends Event {
  /**
   * Creates a new MediaStartedPlayingEvent
   * @param {HTMLElement} resource - The element containing the video that emitted the event
   */
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
    this.detail = {
      resource
    };
  }
}
const _SlideshowSelectEvent = class _SlideshowSelectEvent extends Event {
  /**  @param {SlideshowSelectEventData} data */
  constructor(data) {
    super(_SlideshowSelectEvent.eventName, { bubbles: true });
    /** @type {SlideshowSelectEventData} */
    __publicField(this, "detail");
    this.detail = data;
  }
};
__publicField(_SlideshowSelectEvent, "eventName", "slideshow:select");
let SlideshowSelectEvent = _SlideshowSelectEvent;
class ZoomMediaSelectedEvent extends Event {
  /**
   * Creates a new ZoomMediaSelectedEvent
   * @param {number} index - The index of the selected media
   */
  constructor(index) {
    super(ThemeEvents.zoomMediaSelected, { bubbles: true });
    this.detail = {
      index
    };
  }
}
class MegaMenuHoverEvent extends Event {
  constructor() {
    super(ThemeEvents.megaMenuHover, { bubbles: true });
  }
}
class FilterUpdateEvent extends Event {
  /** @param {URLSearchParams} queryParams */
  constructor(queryParams) {
    super(ThemeEvents.FilterUpdate, { bubbles: true });
    this.detail = {
      queryParams
    };
  }
  shouldShowClearAll() {
    return [...this.detail.queryParams.entries()].filter(([key]) => key.startsWith("filter.")).length > 0;
  }
}
export {
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  DiscountUpdateEvent,
  FilterUpdateEvent,
  MediaStartedPlayingEvent,
  MegaMenuHoverEvent,
  QuantitySelectorUpdateEvent,
  SlideshowSelectEvent,
  ThemeEvents,
  VariantSelectedEvent,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent
};
